import React, { useRef, useContext, useEffect, useState } from 'react';
import TooltipAnchorSVG from './TooltipAnchorSVG';
import Icon from '@ifit/react-icons/dist/components/icon';
import INFO_CIRCLE_SOLID from '@ifit/react-icons/dist/icons/INFO_CIRCLE_SOLID';
import { withTheme } from '@ifit/themes/withTheme';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import {
  shark,
  white,
  scienceBlue,
  mountainMeadow,
} from '@ifit/base-coat/js/colors';
import './index.scss';
import classNames from 'classnames';

export interface IProps {
  info: string;
  maxWidth?: number;
  horizontalPosition?: 'left' | 'right';
  verticalPosition?: 'bottom';
  container?: HTMLElement;
  className?: string;
}

interface ICOLORS {
  [property: string]: {
    [property: string]: string;
  };
}

const COLORS: ICOLORS = {
  LIGHT: {
    iconColor: shark,
    focusedIconColor: scienceBlue,
  },
  DARK: {
    iconColor: white,
    focusedIconColor: mountainMeadow,
  },
  VAULT: {
    iconColor: shark,
    focusedIconColor: scienceBlue,
  },
};

const InfoPanel: React.FC<IProps> = ({
  info,
  horizontalPosition,
  verticalPosition,
  className,
  maxWidth = 440,
  container = document.body,
}: IProps) => {
  const { theme } = useContext(ThemeContext);
  const initialIconColor = COLORS[theme].iconColor;
  const [iconColor, setIconColor] = useState(initialIconColor);
  const [panelWidth, setPanelWidth] = useState(maxWidth);
  const infoPanel = useRef<HTMLDivElement>(null);
  const panelWrapper = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const tooltipAnchor = useRef<HTMLDivElement>(null);
  let updatedPanelWidth: number;
  let updatedPanelWrapperStyles: {
    [key: string]: string;
  };
  const [infoPanelHasFocus, setInfoPanelHasFocus] = useState(false);

  const handleIconWrapperClick = (): void => {
    if (!infoPanelHasFocus) {
      setInfoPanelHasFocus(true);
    }
    if (infoPanelHasFocus) {
      setInfoPanelHasFocus(false);
    }
  };

  useEffect(() => {
    if (infoPanelHasFocus) {
      infoPanel?.current?.focus();
    }
    if (!infoPanelHasFocus) {
      infoPanel?.current?.blur();
    }
  }, [infoPanelHasFocus]);

  const handleInfoPanelBlur = (): void => {
    setInfoPanelHasFocus(false);
    updateIconColor();
  };

  const handleInfoPanelFocus = (): void => {
    updateIconColor();
  };

  const updateIconColor = (): void => {
    if (document.activeElement === infoPanel?.current) {
      setIconColor(COLORS[theme].focusedIconColor);
    } else {
      setIconColor(COLORS[theme].iconColor);
    }
  };

  const containerRect = (): { width: number; left: number } => {
    const width = container?.getBoundingClientRect().width;
    const left = container?.getBoundingClientRect().left;

    return {
      width: width,
      left: left,
    };
  };

  const panelGutter = (): { total: number; left: number; right: number } => {
    const paddingLeft = Number.parseInt(
      window.getComputedStyle(panelWrapper?.current).paddingLeft
    );
    const paddingRight = Number.parseInt(
      window.getComputedStyle(panelWrapper?.current).paddingRight
    );
    const marginLeft = Number.parseInt(
      window.getComputedStyle(panelWrapper?.current).marginLeft
    );
    const marginRight = Number.parseInt(
      window.getComputedStyle(panelWrapper?.current).marginRight
    );
    return {
      total: paddingLeft + paddingRight + marginLeft + marginRight,
      left: paddingLeft + marginLeft,
      right: paddingRight + marginRight,
    };
  };

  const applyStyles = (element, styles): void => {
    Object.assign(element.style, styles);
  };

  const resetStyles = (updatedPanelWidth): void => {
    updatedPanelWrapperStyles = {
      left: '',
      right: '',
      transform: '',
    };

    if (panel.current.style.maxWidth !== updatedPanelWidth) {
      setPanelWidth(updatedPanelWidth);
    }
    applyStyles(panelWrapper.current, updatedPanelWrapperStyles);
  };

  const checkAndSetStyles = (): void => {
    let panelWrapperLeftOffset: number;
    let panelWrapperRightOffset: number;
    let distanceFromContainerLeftToToolTipLeft: number;
    const infoPanelWidth = infoPanel.current?.getBoundingClientRect().width;
    const tooltipAnchorWidth = 24;
    const panelPaddingRight = Number.parseInt(
      window.getComputedStyle(panel?.current).paddingRight
    );
    const panelPaddingLeft = Number.parseInt(
      window.getComputedStyle(panel?.current).paddingRight
    );
    const infoPanelToolTipAnchorDiff =
      (tooltipAnchorWidth - infoPanelWidth) / 2;
    const infoPanelRight = infoPanel.current?.getBoundingClientRect().right;
    const panelWrapperLeft = panelWrapper.current?.getBoundingClientRect().left;
    const panelWrapperRight = panelWrapper.current?.getBoundingClientRect()
      .right;

    if (horizontalPosition === 'right') {
      let panelIsAtRightEdge: boolean;
      panelWrapperLeftOffset =
        panelPaddingLeft + infoPanelToolTipAnchorDiff + panelGutter().left;
      distanceFromContainerLeftToToolTipLeft =
        panelWrapperLeft - containerRect().left;

      if (panelGutter().left) {
        updatedPanelWidth =
          containerRect().width -
          distanceFromContainerLeftToToolTipLeft -
          panelGutter().left -
          panelGutter().right;
        panelIsAtRightEdge =
          containerRect().width <=
          distanceFromContainerLeftToToolTipLeft +
            maxWidth +
            panelGutter().left +
            panelGutter().right
            ? true
            : false;
      } else {
        updatedPanelWidth =
          containerRect().width - distanceFromContainerLeftToToolTipLeft;
        panelIsAtRightEdge =
          containerRect().width <=
          distanceFromContainerLeftToToolTipLeft + maxWidth
            ? true
            : false;
      }

      if (panelIsAtRightEdge) {
        if (updatedPanelWidth !== panelWidth) {
          setPanelWidth(updatedPanelWidth);
        }
      } else {
        updatedPanelWidth = maxWidth;
        resetStyles(updatedPanelWidth);
      }

      updatedPanelWrapperStyles = {
        left: (-panelWrapperLeftOffset).toString().concat('px'),
      };
      applyStyles(panelWrapper.current, updatedPanelWrapperStyles);
    } else if (horizontalPosition === 'left') {
      let panelIsAtLeftEdge: boolean;
      panelWrapperRightOffset =
        panelPaddingRight + infoPanelToolTipAnchorDiff + panelGutter().left;
      distanceFromContainerLeftToToolTipLeft =
        panelWrapperLeft - containerRect().left;

      if (panelGutter().left) {
        panelIsAtLeftEdge =
          distanceFromContainerLeftToToolTipLeft <= 0 ? true : false;
        updatedPanelWidth =
          panelWrapperRight -
          containerRect().left -
          panelGutter().left -
          panelGutter().right;
      } else {
        panelIsAtLeftEdge =
          distanceFromContainerLeftToToolTipLeft <= 0 ? true : false;
        updatedPanelWidth = panelWrapperRight - containerRect().left;
      }

      if (panelIsAtLeftEdge) {
        if (updatedPanelWidth !== panelWidth) {
          setPanelWidth(updatedPanelWidth);
        }
      } else {
        updatedPanelWidth = maxWidth;
        resetStyles(updatedPanelWidth);
      }

      updatedPanelWrapperStyles = {
        left: 'auto',
        right: (-panelWrapperRightOffset).toString().concat('px'),
      };
      applyStyles(panelWrapper.current, updatedPanelWrapperStyles);
    } else {
      let panelIsAtLeftEdge;
      let panelIsAtRightEdge;
      const infoPanelCenter = infoPanelRight - infoPanelWidth / 2;
      const iconIsLeftOfCenter =
        infoPanelCenter - containerRect().left < containerRect().width / 2;
      const iconIsRightOfCenter =
        infoPanelCenter - containerRect().left > containerRect().width / 2;

      if (iconIsLeftOfCenter) {
        panelIsAtLeftEdge =
          infoPanelCenter - containerRect().left <
          maxWidth / 2 + panelGutter().total
            ? true
            : false;
      } else if (iconIsRightOfCenter) {
        panelIsAtRightEdge =
          containerRect().width - (infoPanelCenter - containerRect().left) <
          maxWidth / 2 + panelGutter().right
            ? true
            : false;
      }

      if (iconIsLeftOfCenter || iconIsRightOfCenter) {
        if (panelIsAtLeftEdge || panelIsAtRightEdge) {
          let leftValue: string;
          updatedPanelWidth = containerRect().width - panelGutter().total;
          if (panelGutter().left) {
            leftValue = (
              -infoPanelRight +
              containerRect().left +
              panelGutter().left
            )
              .toString()
              .concat('px');
          } else {
            leftValue = (
              -infoPanelRight +
              infoPanelWidth +
              containerRect().left +
              panelGutter().left
            )
              .toString()
              .concat('px');
          }

          if (updatedPanelWidth !== panelWidth) {
            setPanelWidth(updatedPanelWidth);
          }

          updatedPanelWrapperStyles = {
            left: leftValue,
            transform: 'none',
          };
          applyStyles(panelWrapper.current, updatedPanelWrapperStyles);
        } else {
          updatedPanelWidth = maxWidth;
          resetStyles(updatedPanelWidth);
        }
      } else if (containerRect().width < maxWidth + panelGutter().total) {
        updatedPanelWidth = containerRect().width - panelGutter().total;

        if (updatedPanelWidth !== panelWidth) {
          setPanelWidth(updatedPanelWidth);
        }
      }
    }
  };

  function usecheckAndSetStyles() {
    useEffect(() => {
      checkAndSetStyles();
      let timeoutId;
      const resizeListener = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => checkAndSetStyles(), 1);
      };
      window.addEventListener('resize', resizeListener);

      return () => {
        window.removeEventListener('resize', resizeListener);
      };
    });
  }

  usecheckAndSetStyles();

  return (
    <div
      className={classNames('info-panel', className)}
      data-testid="info-panel"
      tabIndex={0}
      ref={infoPanel}
      onFocus={handleInfoPanelFocus}
      onBlur={handleInfoPanelBlur}
    >
      <div
        className="icon-wrapper"
        data-testid="icon-wrapper"
        onClick={handleIconWrapperClick}
      >
        <Icon
          icon={INFO_CIRCLE_SOLID}
          className="panel-icon"
          onClick={checkAndSetStyles}
          color={iconColor}
          size={16}
        ></Icon>
      </div>
      <div
        className={classNames(
          'panel-wrapper',
          horizontalPosition,
          verticalPosition
        )}
        ref={panelWrapper}
      >
        <div
          className={classNames('panel')}
          data-testid="panel"
          style={{ maxWidth: panelWidth }}
          ref={panel}
        >
          {info}
        </div>
      </div>
      <div ref={tooltipAnchor}>
        <TooltipAnchorSVG
          className={classNames('tooltip-anchor', verticalPosition)}
        />
      </div>
    </div>
  );
};

export default withTheme(InfoPanel);
